<script lang="ts">
  import Button from '../ui/Button.svelte';
  import { CHAT_MAX_LENGTH } from '../../shared/config';
  import type { ChatMessage } from '../../shared/protocol';

  interface Props {
    messages: readonly Readonly<ChatMessage>[];
    meId: string;
    sendChat: (text: string) => boolean;
    connected: boolean;
  }
  let { messages, meId, sendChat, connected }: Props = $props();

  /** Staying within this distance of the bottom counts as following the conversation. */
  const PINNED_THRESHOLD_PX = 24;

  let draft = $state('');
  let log = $state<HTMLUListElement | null>(null);
  let pinned = $state(true);
  const canSend = $derived(connected && draft.trim().length > 0);

  function handleScroll(): void {
    if (!log) return;
    pinned = log.scrollHeight - log.scrollTop - log.clientHeight < PINNED_THRESHOLD_PX;
  }

  function followLatest(): void {
    if (pinned) log?.scrollTo({ top: log.scrollHeight });
  }

  $effect(() => {
    void messages.at(-1)?.id;
    followLatest();
  });

  // A soft keyboard or rotation shrinks the log without a new message; stay on the latest line.
  $effect(() => {
    if (!log) return;
    const observer = new ResizeObserver(followLatest);
    observer.observe(log);
    return () => observer.disconnect();
  });

  function submit(event: SubmitEvent): void {
    event.preventDefault();
    const text = draft.trim();
    // The transport reports a closed socket; keep the draft so nothing typed is lost.
    if (!text || !sendChat(text)) return;
    draft = '';
    pinned = true;
  }
</script>

<div class="chat">
  {#if messages.length}
    <ul
      bind:this={log}
      class="chat-log"
      aria-label="Room chat"
      aria-live="polite"
      aria-relevant="additions"
      onscroll={handleScroll}
    >
      {#each messages as message (message.id)}
        {#if message.kind === 'system'}
          <li class="system"><span>{message.text}</span></li>
        {:else}
          <li class:own={message.playerId === meId}>
            <span class="who">{message.avatar} {message.name}</span>
            <span class="text">{message.text}</span>
          </li>
        {/if}
      {/each}
    </ul>
  {:else}
    <p class="hint">No messages yet.</p>
  {/if}
  <form class="chat-row" onsubmit={submit}>
    <input
      aria-label="Message the room"
      bind:value={draft}
      maxlength={CHAT_MAX_LENGTH}
      autocomplete="off"
      autocapitalize="sentences"
      enterkeyhint="send"
      placeholder="Message the room"
      disabled={!connected}
    />
    <Button type="submit" size="small" disabled={!canSend}>Send</Button>
  </form>
  {#if !connected}<p class="hint">Reconnecting… send again once you're back.</p>{/if}
</div>

<style>
  .chat {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 auto;
    min-height: 0;
  }
  .chat-log {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .chat-log li {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 14px;
  }
  .chat-log li.system {
    align-items: center;
    text-align: center;
    font-size: 12px;
    color: var(--theme-muted-text);
  }
  .who {
    font-weight: 700;
    font-size: 12px;
    color: var(--theme-muted-text);
  }
  .own .who {
    color: var(--theme-accent);
  }
  .text {
    overflow-wrap: anywhere;
  }
  .chat-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
    flex-shrink: 0;
  }
  /* Body-sized composer: the field-size token suits forms, not a message row. 16px keeps iOS from zooming on focus. */
  .chat-row input {
    flex: 1 1 auto;
    min-width: 0;
    padding: 8px 12px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.3;
  }
</style>
